import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import moment from 'moment'
import { forEach, get, startCase } from 'lodash'
import apiGet from 'lib/network/apiGet'
import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import Popover from 'components/molecules/Popover'
import { formateDate } from 'utils'
import notify from 'components/atoms/Notify'
import { IoSearch, IoCalendar } from 'react-icons/io5'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'

import useModal from 'hooks/useModal'

import apiDelete from 'lib/network/apiDelete'
import { DateRangePicker } from 'react-date-range'
import { Tooltip, Select } from 'antd'

import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'

import apiPost from 'lib/network/apiPost'
import useParams from 'hooks/useGetParams'
import { CloudUploadIcon, ChevronDownIcon } from '@heroicons/react/outline'
import PageFooter from 'components/paginationComponent'
import Selection from 'components/molecules/Selection'
import apiGetBlob from 'lib/network/apiGetBlob'

const SalesReports = () => {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  const [filter, setFilter] = useState({ workspace: -1 })
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })

  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [pagination, setPagination] = useState({})
  const [importManagment, setImportManagment] = useState([])
  const [companyNames, setCompanyNames] = useState([])

  const [uploading, setUploading] = useState(false)
  const [search, setSearch] = useState('')

  const [companies, setCompanies] = useState([])
  const [branches, setBranches] = useState([])
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingBranches, setLoadingBranches] = useState(false)
  const [selectedReportType, setSelectedReportType] = useState('')
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState([])
  const [selectedBookingStatus, setSelectedBookingStatus] = useState([])

  const popover = useRef()

  const reportTypes = [{ id: 'sales', name: t('salesReports.reportTypes.sales') }]

  const paymentStatuses = [
    { id: 'paid', name: 'Paid' },
    { id: 'unpaid', name: 'Unpaid' },
    { id: 'partialpaid', name: 'Partial Paid' },
    { id: 'failure', name: 'Failure' },
    { id: 'refund', name: 'Refund' },
  ]

  const bookingStatuses = [
    { id: 'pending', name: 'Pending' },
    { id: 'confirmed', name: 'Confirmed' },
    { id: 'cancelled', name: 'Cancelled' },
    { id: 'rejected', name: 'Rejected' },
    { id: 'completed', name: 'Completed' },
  ]

  useEffect(() => {
    setSelectedBookingStatus(bookingStatuses.map(status => status.id))
  }, [])

  useEffect(() => {
    setSelectedPaymentStatus(paymentStatuses.map(paymentStatus => paymentStatus.id))
  }, [])
  useEffect(() => {
    fetchCompanies()
  }, [])

  const fetchCompanies = async () => {
    setLoadingCompanies(true)
    const response = await apiGet('companies', { pageSize: 1000 })
    if (response?.data) {
      const companies = response.data.company.map(company => ({
        id: company._id,
        name: company.name,
      }))
      setCompanyNames(companies)
    } else {
      setCompanyNames([])
    }
    setLoadingCompanies(false)
  }

  const fetchBranches = async companyId => {
    try {
      setLoadingBranches(true)
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.data) {
        const branchList = response.data.map(branch => ({
          id: branch._id,
          name: branch.name,
        }))
        setBranches(branchList)
      } else {
        setBranches([])
      }
      setLoadingBranches(false)
    } catch (error) {
      notify.error(t('salesReports.messages.fetchBranchesFailed'))
      setBranches([])
      setLoadingBranches(false)
    }
  }

  const handleCompanyChange = value => {
    setSelectedCompany(value)
    setSelectedBranch(null)
    if (value) {
      fetchBranches(value)
    } else {
      setBranches([])
    }
  }

  useEffect(() => {
    fetchImportManagment()
  }, [filter])

  const fetchImportManagment = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    }

    const params = {}
    params.pageNumber = page
    params.pageSize = 20
    if (filter.sortBy && filter.sortOrder) {
      params.sortBy = filter.sortBy
      params.sortOrder = filter.sortOrder
    }

    const response = await apiGet('reports', params)
    setImportManagment(page === 1 ? response.data.report : [...importManagment, ...response.data.report])
    setPagination(response?.data?.pagination || [])

    setLoading(false)
  }

  const handleSelect = range => {
    setDateRange({
      startDate: moment(range.selection.startDate),
      endDate: moment(range.selection.endDate),
    })
  }

  const handleReportDownload = async (reportId, format = 'pdf') => {
    if (!reportId) return

    try {
      const blob = await apiGetBlob(`reports/${reportId}/download?format=${format}`)

      if (!blob) {
        return
      }

      const fileExtension = format === 'excel' || format === 'xlsx' ? 'xlsx' : 'pdf'
      const fileName = `sales_report_${reportId || 'download'}.${fileExtension}`

      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error downloading report:', error)
    }
  }

  const generateSalesReport = async () => {
    const missingFields = []
    if (!selectedReportType) missingFields.push(t('salesReports.form.reportType'))

    if (missingFields.length > 0) {
      notify.error(t('salesReports.messages.missingRequiredFields'), t('salesReports.messages.missingFieldsList', { fields: missingFields.join(', ') }))
      return
    }

    try {
      setGenerating(true)

      const payload = {
        company: selectedCompany,
        branch: selectedBranch,
        type: selectedReportType,
        paymentStatus: selectedPaymentStatus?.length ? selectedPaymentStatus : paymentStatuses.map(paymentStatus => paymentStatus.id),
        bookingStatus: selectedBookingStatus?.length ? selectedBookingStatus : bookingStatuses.map(status => status.id),
        reportStartDate: dateRange.startDate ? dateRange.startDate.format('YYYY-MM-DD') : null,
        reportEndDate: dateRange.endDate ? dateRange.endDate.format('YYYY-MM-DD') : null,
      }

      const response = await apiPost('transactions/sales-report', payload)

      if (response.success) {
        notify.success(t('salesReports.title'), t('salesReports.messages.reportGeneratedSuccess'))
        fetchImportManagment()

        if (response.data?._id || response.data?.id) {
          const reportId = response.data._id || response.data.id
          // Determine format: prefer PDF if available, otherwise Excel
          const format = response.data?.pdfUrl ? 'pdf' : 'excel'
          handleReportDownload(reportId, format)
        }

        setSelectedCompany(null)
        setSelectedBranch(null)
        setSelectedReportType('')
        setSelectedPaymentStatus(paymentStatuses.map(paymentStatus => paymentStatus.id))
        setSelectedBookingStatus(bookingStatuses.map(status => status.id))
        setDateRange({ startDate: null, endDate: null })
        setBranches([])
      } else {
        notify.error(response?.data?.message)
      }
    } catch (error) {
      notify.error(t('salesReports.messages.reportGenerationFailed'))
    } finally {
      setGenerating(false)
    }
  }

  const handleReportTypeChange = value => {
    setSelectedReportType(value?.id)
  }

  const columns = [
    {
      width: '10%',
      name: t('salesReports.columns.generatedBy'),
      key: 'generatedBy',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.generatedBy === 'system' ? t('salesReports.columns.system') : item?.user?.fullName || 'Admin'}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('salesReports.columns.reportType'),
      key: 'type',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.type}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('salesReports.columns.company'),
      key: 'company',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.company?.name || t('salesReports.columns.allCompanies')}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('salesReports.columns.branch'),
      key: 'branch',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item?.branch?.name || t('salesReports.columns.allBranches')}</span>
            </div>
          </>
        )
      },
    },
    {
      width: '10%',
      name: t('salesReports.columns.dateRange'),
      key: 'range',
      sortable: true,
      rendered: item => {
        const formatDate = dateStr => {
          if (!dateStr) return ''
          const date = new Date(dateStr)
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
        }

        return (
          <div>
            <span className="text-[13px]">
              {item?.startDate || item?.endDate ? `${formatDate(item?.startDate)} - ${formatDate(item?.endDate)}` : t('salesReports.columns.allDates')}
            </span>
          </div>
        )
      },
    },

    {
      name: t('salesReports.columns.salesReport'),
      width: '10%',
      key: 'name',
      sortable: false,
      rendered: item => {
        const reportId = item._id || item.id
        if (!reportId) return null

        const format = item?.pdfUrl ? 'pdf' : item?.excelUrl ? 'excel' : 'pdf'

        return (
          <div>
            <button onClick={() => handleReportDownload(reportId, format)} className="text-sm text-normal text-blue-400 hover:text-blue-600 underline cursor-pointer">
              {t('salesReports.columns.downloadReport')}
            </button>
          </div>
        )
      },
    },

    {
      width: '5%',
      name: t('salesReports.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : 'Registered'

        return (
          <div className="text-sm text-gray-500">
            <Tooltip title={createdTime ? formateDate(moment(createdTime)) : 'N/A'}>{createdAt}</Tooltip>
          </div>
        )
      },
    },

    {
      width: '5%',
      name: t('salesReports.columns.updated'),
      key: 'updated',
      sortable: true,
      rendered: item => {
        const updatedTime = get(item, 'updatedAt', '')
        const updatedAt = updatedTime ? moment(updatedTime).fromNow() : 'Registered'

        return (
          <div className="text-sm text-gray-500">
            <Tooltip title={updatedTime ? formateDate(moment(updatedTime)) : 'N/A'}>{updatedAt}</Tooltip>
          </div>
        )
      },
    },
  ]

  return (
    <Layout
      headerRight={
        <div className="flex items-center justify-end gap-4">
          <div className="mt-1 relative rounded-md shadow-sm flex-grow">
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
          </div>
        </div>
      }
      title={t('salesReports.title')}
    >
      <div className="space-y-6 sm:space-y-5 pt-8">
        {/* Responsive grid for form fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 ml-2 md:ml-4">
          <div>
            <label htmlFor="company" className="block text-sm font-normal text-gray-700 mb-1">
              {t('salesReports.form.company')}
            </label>
            <Selection
              title={t('salesReports.form.selectCompanies')}
              list={companyNames}
              className="w-full"
              onChange={value => handleCompanyChange(value?.id || null)}
              value={selectedCompany || ''}
            />
          </div>
          <div>
            <label htmlFor="branch" className="block text-sm font-normal text-gray-700 mb-1">
              {t('salesReports.form.branch')}
            </label>
            <Selection
              title={t('salesReports.form.selectBranch')}
              list={branches}
              onChange={value => setSelectedBranch(value?.id || null)}
              value={selectedBranch || ''}
              className="w-full"
              disabled={!selectedCompany || loadingBranches}
            />
          </div>
          <div>
            <label htmlFor="type" className="block text-sm font-normal text-gray-700 mb-1">
              {t('salesReports.form.reportType')}
            </label>
            <Selection title={t('salesReports.form.selectReportType')} list={reportTypes} className="w-full" onChange={handleReportTypeChange} value={selectedReportType} />
          </div>
          {/* <div>
            <label htmlFor="type" className="block text-sm font-normal text-gray-700 mb-1">
              {'Select Payment Status'}
            </label>
            <Selection title={'Select Payment Status'} list={paymentStatus} className="w-full" onChange={handlePaymentStatusChange} value={selectedPaymentStatus} />
          </div> */}
          <div>
            <label htmlFor="booking-status" className="block text-sm font-normal text-gray-700 mb-1">
              {'Payment Status'}
            </label>
            <Selection
              title={'Select Payment Status'}
              list={paymentStatuses}
              className="w-full"
              multiple={true}
              onChange={ids => setSelectedPaymentStatus(ids || [])}
              value={selectedPaymentStatus}
            />
          </div>
          <div>
            <label htmlFor="booking-status" className="block text-sm font-normal text-gray-700 mb-1">
              {'Booking Status'}
            </label>
            <Selection
              title={'Select Booking Status'}
              list={bookingStatuses}
              className="w-full"
              multiple={true}
              onChange={ids => setSelectedBookingStatus(ids || [])}
              value={selectedBookingStatus}
            />
          </div>

          <div>
            <label htmlFor="date-range" className="block text-sm font-medium text-gray-700 mb-1">
              {t('salesReports.form.dateRange')}
            </label>
            <Popover
              custom
              placement="bottom-start"
              ref={popover}
              content={close => (
                <div className=" max-w-[90vw] sm:max-w-none">
                  <DateRangePicker
                    ranges={[
                      {
                        startDate: dateRange.startDate ? dateRange.startDate.toDate() : new Date(),
                        endDate: dateRange.endDate ? dateRange.endDate.toDate() : new Date(),
                        key: 'selection',
                      },
                    ]}
                    onChange={handleSelect}
                  />

                  <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
                    <button
                      onClick={close}
                      className="w-full sm:w-[100px] py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                    >
                      {t('salesReports.buttons.close')}
                    </button>
                    <button
                      disabled={!dateRange.startDate && !dateRange.endDate}
                      onClick={() => {
                        setDateRange({ startDate: null, endDate: null })
                        close()
                      }}
                      className="w-full sm:w-[100px] py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none"
                    >
                      {t('salesReports.buttons.clear')}
                    </button>
                  </div>
                </div>
              )}
            >
              <button
                id="date-range"
                type="button"
                className="inline-flex w-full h-[38px] justify-between items-center border border-gray-300 shadow-sm text-sm leading-4 font-regular rounded-md text-gray-700 bg-white hover:bg-gray-50 outline-none px-3"
              >
                <div className="flex items-center">
                  <IoCalendar className="h-4 w-4 mr-2 text-gray-500 flex-shrink-0" />
                  <span className="truncate">
                    {dateRange.startDate && dateRange.endDate
                      ? `${dateRange.startDate.format('MM-DD-YYYY')} to ${dateRange.endDate.format('MM-DD-YYYY')}`
                      : t('salesReports.form.selectDateRange')}
                  </span>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
              </button>
            </Popover>
          </div>
        </div>

        {/* Generate button */}
        <div className="mt-3 ml-2 md:ml-4">
          <Button onClick={generateSalesReport} disabled={generating} className="w-full sm:w-auto">
            {generating ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-3 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="white"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t('salesReports.buttons.generatingReport')}
              </>
            ) : (
              t('salesReports.buttons.generateReport')
            )}
          </Button>
        </div>

        {/* Table container with responsive overflow */}
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full overflow-x-auto">
                {!loading ? (
                  <>
                    {importManagment?.length === 0 ? (
                      <EmptyState v2 description={t('salesReports.messages.noReportsFound')} />
                    ) : (
                      <div className="min-w-[800px]">
                        <Table
                          hasMore={pagination?.currentPage !== pagination?.totalPages}
                          fetchData={() => fetchImportManagment(pagination?.currentPage + 1)}
                          sortBy={filter.sortBy}
                          sortOrder={filter.sortOrder}
                          handleSort={(sortBy, sortOrder) => {
                            setFilter(prevFilter => ({ ...prevFilter, sortBy, sortOrder }))
                          }}
                          data={importManagment}
                          columns={columns}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default SalesReports
